import { unescape } from "lodash";
import URI from "urijs";

import type { BusbudError } from "@busbud/busbud-errors";

import type { TranslatorService } from "@app/services/translator";

function getUnverifiedProviderIdentityErrorText(
  translator: TranslatorService,
  error: BusbudError | undefined
): string {
  if (error?.provider_name === "facebook") {
    return translator.t(
      "!account.authentication.provider.identity-error.facebook"
    );
  }

  return translator.t("!account.authentication.provider.identity-error");
}

export function getErrorText(
  translator: TranslatorService,
  error_code: string = "",
  error?: BusbudError
): string {
  switch (error_code) {
    case "UnverifiedProviderIdentityError":
      return getUnverifiedProviderIdentityErrorText(translator, error);
    case "UserEmailVerificationRequired":
      return translator.t("!account.sign-up.user-email-verification-required");
    case "EmailUserExists":
      return translator.t("!error.EmailUserExists.text");
    case "NoUser":
      return translator.t("!error.NoUser.text");
    case "SignInWithEmail":
      return translator.t("!error.SignInWithEmail.text");
    case "FBUserExists":
      return translator.t("!error.FBUserExists.text");
    case "GoogleUserExists":
      return translator.t("!error.GoogleUserExists.text");
    case "AppleUserExists":
      return translator.t("!error.AppleUserExists.text");
    case "PasswordInvalid":
      return translator.t("!error.PasswordInvalid.text");
    case "UserNotVerified":
      return translator.t("!error.UserNotVerified.text");
    case "UserAlreadyVerified":
      return translator.t("!error.UserAlreadyVerified.text");
    case "UserVerificationTokenInvalid":
      return translator.t("!error.UserVerificationTokenInvalid.text");
    case "InputInvalid":
      return translator.t("!error.InputInvalid.text");
    case "TooManyRequests":
      return translator.t("!error.password_reset.rate_limit");
    case "MissingInfoNewPassenger":
      return translator.t("!accounts.passengers.save_passenger_error");
    case "FieldRequired":
      return "";
    case "Conflict":
    case "update_passenger.conflict":
      return translator.t("!error.update_passenger.conflict.text");
    case "delete_passenger.failed":
      return translator.t("!error.delete_passenger.failed.text");
    case "email.invalid_email":
      return translator.t("!error.email.invalid_email.text");
    case "confirm_email.emails_do_not_match":
      return translator.t(
        "!accounts.update-email.form-validation.equal-to-email"
      );
    case "email.required":
      return translator.t("!error.email.required.text");
    case "first_name.invalid_name":
      return translator.t("!error.first_name.invalid_name.text");
    case "first_name.required":
      return translator.t("!error.first_name.required.text");
    case "first_name.too_short":
      return translator.t("!error.first_name.too_short.text");
    case "first_name.too_long":
      return translator.t("!error.first_name.too_long.text");
    case "last_name.invalid_name":
      return translator.t("!error.last_name.invalid_name.text");
    case "last_name.required":
      return translator.t("!error.last_name.required.text");
    case "last_name.too_short":
      return translator.t("!error.last_name.too_short.text");
    case "last_name.too_long":
      return translator.t("!error.last_name.too_long.text");
    case "password.required":
    case "confirmed_password.required":
      return translator.t("!error.password.required.text");
    case "password.too_short":
    case "confirmed_password.too_short":
      return translator.t("!error.password.too_short.text");
    case "password.signin.required":
      return translator.t("!error.password.required.text");
    case "password.signup.required":
      return translator.t("!error.password.required.text");
    case "password.signin.too_short":
      return translator.t("!error.password.too_short.text");
    case "password.signup.too_short":
      return translator.t("!error.password.signup.too_short.text");
    case "password.signup.too_long":
      return translator.t("!error.password.signup.too_long.text");
    case "password.passwords_do_not_match":
    case "confirmed_password.passwords_do_not_match":
      return translator.t(
        "!account.reset-password.form-validation.equal-to-password"
      );
    case "password.invalid":
      return translator.t("!error.PasswordInvalid.text");
    case "PasswordResetEmailForm":
      return translator.t("!error.PasswordResetEmailForm.text");
    case "PasswordResetTokenInvalid":
      return translator.t("!error.PasswordResetTokenInvalid.text");
    case "dob.invalid_date":
      return translator.t("!error.dob.invalid_date.text");
    case "dob.dob_in_future":
      return translator.t("!error.dob.dob_in_future.text");
    default:
      // TODO: create an error for generic form errors.
      return translator.t("!error.BadRequest.title");
  }
}

export function isEmailFieldError(error_type: string): boolean {
  return ["email.invalid_email", "email.required"].includes(error_type);
}

export function isPasswordFieldError(error_type: string): boolean {
  return ["password.too_short", "password.required"].includes(error_type);
}

/**
 * Unescapes a redirect query param, if available, otherwise returns null
 *
 * @param query_param
 * @returns resource_uri
 */
export function getUnescapedRedirectResource(
  query_param: string
): string | null {
  if (!query_param) {
    return null;
  }

  // Unescape is to workaround use of sanitizeQuery middleware in app.js
  const redirect_address = unescape(decodeURIComponent(query_param));

  // A 'resource' is everything following busbud.com, e.g. "/en/account?query=foo#other"
  const redirect_ressource = URI(redirect_address).resource();

  return redirect_ressource;
}
